import type { DataProvider, RaRecord, SortPayload } from "react-admin";

/** Matches Fakhri-api `BaseCrudService.getLimit` cap — paginate at this size. */
export const LIST_EXPORT_PAGE_SIZE = 500;

/** Safety guard against runaway pagination (500 × 200 = 100k rows). */
const MAX_EXPORT_PAGES = 200;

export type FetchAllListRowsParams = {
  sort?: SortPayload;
  filter?: Record<string, unknown>;
  meta?: Record<string, unknown>;
  /** Optional cap on total rows (e.g. ITS export). Omit to fetch all matching rows. */
  maxResults?: number;
};

/**
 * Fetches every row for a react-admin list export by paging `getList` until exhausted.
 * The API caps each page at {@link LIST_EXPORT_PAGE_SIZE}. react-admin's list context `getData`
 * only requests one page (capped by the API), so exports were silently truncated.
 *
 * Stops only on an empty page or a short page (< page size). We do not trust API `total` for
 * termination because a full 500-row page can coincide with `total: 500` while more rows exist.
 */
export async function fetchAllListRows(
  dataProvider: DataProvider,
  resource: string,
  params: FetchAllListRowsParams = {}
): Promise<RaRecord[]> {
  const { sort, filter, meta, maxResults } = params;
  const out: RaRecord[] = [];
  let page = 1;

  for (let i = 0; i < MAX_EXPORT_PAGES; i += 1) {
    const { data } = await dataProvider.getList(resource, {
      pagination: { page, perPage: LIST_EXPORT_PAGE_SIZE },
      sort: sort ?? { field: "id", order: "DESC" },
      filter: filter ?? {},
      meta: { ...meta, exportAll: true },
    });

    const chunk = (data ?? []) as RaRecord[];
    if (chunk.length === 0) {
      break;
    }

    out.push(...chunk);

    if (maxResults != null && out.length >= maxResults) {
      return out.slice(0, maxResults);
    }

    if (chunk.length < LIST_EXPORT_PAGE_SIZE) {
      break;
    }

    page += 1;
  }

  return out;
}
